import {
	ETableStructureAlign,
	ETableStructureType,
	TEmValue,
	TTableStructure,
} from 'ui/EasTable/types';
import { TEmVatCorrectionsImportRow } from 'redux/reducers/emVatCorrectionsImport/types';
import { DATES, DATES_ID } from 'services/constants/dates-constants';
import { EIconButtons } from '../../ui/EasTable/EasTableRow/EasTableValue/constants';
import {
	CORRECTION_ACTION_BUTTON_NAME,
	DOWNLOAD_SUPPORTIVE_DOCUMENT_BUTTON_NAME,
} from './VerificationCorrectionActionDialog/constants';
import { EEmVatCorrectionsApprovalStatus } from '../../redux/reducers/emVatCorrectionsImport/constants';
import { EEmEnums } from '../../redux/reducers/emManagement/constants';
import { TUser } from '../../redux/reducers/user/types';

export const correctionPeriodLabel = (item: TEmVatCorrectionsImportRow) => {
	const month = item.month != null ? `${String(item.month).padStart(2, '0')}.` : '';
	const quarter = item.quarter != null ? `Q${item.quarter} ` : '';
	const year = item.year != null ? item.year : '';
	return month != '' || quarter != '' ? `${month}${quarter}${year}` : '';
};

export const tableStructure: TTableStructure<keyof TEmVatCorrectionsImportRow>[] = [
	{
		name: 'ID',
		selector: 'id',
		noName: true,
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
	},
	{
		name: 'Scheme',
		selector: 'scheme',
		show: true,
		sortable: true,
		separateFilter: true,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		filter: [
			['ioss', 'Import'],
			['non_eu_ioss', 'Non EU Import'],
			['non_union', 'Non-Union'],
			['union', 'Union '],
		],
	},

	{
		name: 'Requested at',
		selector: 'requested_on',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
		filter: [
			[DATES_ID.TODAY, DATES.TODAY],
			[DATES_ID.YESTERDAY, DATES.YESTERDAY],
			[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
			[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
			[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
			[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
			[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
			[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
			[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
			[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
			[DATES_ID.CUSTOM_RANGE, DATES.CUSTOM_RANGE],
		],
	},
	{
		name: 'EM ID',
		selector: 'em_id',
		show: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
	},
	{
		name: 'Correction period',
		selector: 'correction_peeriod',
		show: true,
		type: ETableStructureType.STATUS_NO_BG,
		align: ETableStructureAlign.LEFT,
		// value: correctionPeriodLabel,
	},
	{
		name: 'EM name',
		selector: 'full_name',
		show: true,
		sortable: true,
		type: ETableStructureType.EM,
		value: (item: TEmVatCorrectionsImportRow) => ({ id: item.em_id, name: item.full_name }),
		align: ETableStructureAlign.LEFT,
	},

	{
		name: 'Member state',
		selector: 'country',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.COUNTRY_CODE,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Correction value',
		selector: 'requested_value_correction',
		show: true,
		sortable: true,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		currency: (item: TEmVatCorrectionsImportRow) => item.correction_currency ?? 'EUR',
	},
	{
		name: 'Action date',
		selector: 'action_date',
		show: true,
		sortable: true,
		filterable: true,
		filter: [
			[DATES_ID.TODAY, DATES.TODAY],
			[DATES_ID.YESTERDAY, DATES.YESTERDAY],
			[DATES_ID.LAST_7_DAYS, DATES.LAST_7_DAYS],
			[DATES_ID.LAST_30_DAYS, DATES.LAST_30_DAYS],
			[DATES_ID.THIS_MONTH, DATES.THIS_MONTH],
			[DATES_ID.LAST_MONTH, DATES.LAST_MONTH],
			[DATES_ID.THIS_QUARTER, DATES.THIS_QUARTER],
			[DATES_ID.LAST_QUARTER, DATES.LAST_QUARTER],
			[DATES_ID.THIS_YEAR, DATES.THIS_YEAR],
			[DATES_ID.LAST_YEAR, DATES.LAST_YEAR],
			[DATES_ID.CUSTOM_RANGE, DATES.CUSTOM_RANGE],
		],
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Supportive document',
		selector: 'supportive_document',
		show: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
	},
	{
		name: 'Supportive document name',
		selector: 'supportive_document_name',
		show: false,
		sortable: true,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Country ID',
		selector: 'country_id',
		show: false,
		type: ETableStructureType.NUMBER,
		align: ETableStructureAlign.RIGHT,
	},
	{
		name: 'Special scheme number',
		selector: 'ioss_registry_import_scheme_id',
		show: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Commencement date',
		selector: 'ioss_import_scheme_commencement_date',
		show: false,
		type: ETableStructureType.DATE,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Last reported VAT per MS',
		selector: 'last_reported_vat_per_ms',
		noName: true,
		show: false,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
		value: (item: TEmVatCorrectionsImportRow) =>
			item.last_reported_vat_per_ms != null ? JSON.stringify(item.last_reported_vat_per_ms) : '',
	},
	{
		name: 'EAS Order ID',
		selector: 'order_id',
		show: true,
		sortable: true,
		type: ETableStructureType.ORDER_ID,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Related order',
		selector: 'external_order_id',
		show: true,
		sortable: true,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Total order VAT',
		selector: 'total_vat',
		show: true,
		sortable: true,
		type: ETableStructureType.AMOUNT,
		align: ETableStructureAlign.RIGHT,
		currency: (item: TEmVatCorrectionsImportRow) => item.order_currency ?? 'EUR',
	},
	{
		name: 'Order currency',
		selector: 'order_currency',
		show: false,
		sortable: true,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Currency',
		selector: 'currency_id',
		show: false,
		sortable: true,
		type: ETableStructureType.ARRAY_ID,
		align: ETableStructureAlign.LEFT,
		enumName: EEmEnums.SUPPORTED_CURRENCY,
	},

	{
		name: 'Delivery operator',
		selector: 'delivery_operator',
		show: true,
		sortable: true,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Table name',
		selector: 'table_name',
		show: false,
		sortable: true,
		filterable: false,
		colSpan: 1,
		type: ETableStructureType.STRING,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Comments',
		selector: 'action_remarks',
		show: true,
		sortable: true,
		type: ETableStructureType.TEXT,
		align: ETableStructureAlign.LEFT,
	},

	{
		name: 'Approval status',
		selector: 'approval_status',
		show: false,
		sortable: true,
		filterable: false,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
	},

	{
		name: 'Approval status',
		selector: 'approval_status_for_filter',
		show: true,
		sortable: true,
		filterable: true,
		type: ETableStructureType.STATUS,
		align: ETableStructureAlign.LEFT,
		filter: [
			[EEmVatCorrectionsApprovalStatus.APPROVED, 'Approved'],
			[EEmVatCorrectionsApprovalStatus.REJECTED, 'Rejected'],
			[EEmVatCorrectionsApprovalStatus.PENDING, 'Pending'],
		],
		colors: [
			[EEmVatCorrectionsApprovalStatus.APPROVED, 'success.light'],
			[EEmVatCorrectionsApprovalStatus.REJECTED, 'error.light'],
			[EEmVatCorrectionsApprovalStatus.PENDING, 'warning.light'],
		],
		defaultFilterValue: EEmVatCorrectionsApprovalStatus.PENDING,
	},
	{
		name: 'Updated by',
		selector: 'updated_by_user',
		show: true,
		sortable: true,
		filterable: false,
		type: ETableStructureType.USER,
		align: ETableStructureAlign.LEFT,
	},
	{
		name: 'Updated by',
		selector: 'updated_by',
		show: false,
		sortable: true,
		filterable: true,
		type: ETableStructureType.USER,
		align: ETableStructureAlign.LEFT,
		value: (item: TEmVatCorrectionsImportRow) => item.updated_by,
	},

	{
		name: 'Actions',
		selector: 'actions',
		noName: true,
		show: true,
		type: ETableStructureType.BUTTONS,
		align: ETableStructureAlign.LEFT,
		buttons: [
			{
				name: DOWNLOAD_SUPPORTIVE_DOCUMENT_BUTTON_NAME,
				icon: EIconButtons.CLOUD_DOWNLOAD,
			},
			{
				name: CORRECTION_ACTION_BUTTON_NAME,
				icon: EIconButtons.CHECK_CIRCLE,
			},
		],
		buttonFilter: (item: TEmVatCorrectionsImportRow) => {
			const buttons = [];
			buttons.push({
				name: DOWNLOAD_SUPPORTIVE_DOCUMENT_BUTTON_NAME,
				icon: EIconButtons.CLOUD_DOWNLOAD,
			});
			if (item.approval_status_for_filter === EEmVatCorrectionsApprovalStatus.PENDING)
				buttons.push({
					name: CORRECTION_ACTION_BUTTON_NAME,
					icon: EIconButtons.RULE,
				});
			return buttons;
		},
	},
];
